import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Cable, Monitor, Network, Camera, Server, ChevronRight, ArrowRight, MessageCircle } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";

const waLinkFor = (product) =>
  `https://wa.me/918500914557?text=${encodeURIComponent(
    `Hi Jain Infotech, I would like a quote for ${product || "IT products"}.`
  )}`;

const CATEGORIES = [
  {
    id: "cables", name: "Cables & Connectivity", Icon: Cable, color: "#0F62FE", bg: "#EFF6FF",
    img: "https://static.prod-images.emergentagent.com/jobs/807d24b9-8a0d-4ee2-b221-042517f188cc/images/f6f8147bf636535a69696aa8501aad4936c337c5b5afa664abf153101ce988f2.png",
    desc: "Premium HDMI, VGA, DisplayPort, USB & power cabling solutions for every enterprise infrastructure need",
    subcategories: [
      { name: "HDMI Cables & Extenders", items: [
        { name: "HDMI 8K Cable 2.1 ver",              img: "/assets/generated/hdmi-8k-2-1.png" },
        { name: "HDMI 4K Cable 2.0 ver",              img: "/assets/generated/hdmi-4k-2-0.png" },
        { name: "HDMI 4K AOC Cable 2.0 ver Armoured", img: "/assets/generated/hdmi-4k-aoc-armoured.png" },
        { name: "HDMI 4K Extenders & KVM Extenders",  img: "/assets/generated/hdmi-extenders-kvm.png" },
        { name: "HDMI 4K Switchers, Matrix & Splitters", img: "/assets/generated/hdmi-switch-matrix-splitter.png" },
      ] },
      { name: "VGA Cables", items: [
        { name: "VGA Cable Economy Series",     img: "/assets/generated/vga-economy.png" },
        { name: "VGA Copper Cable Gold Series", img: "/assets/generated/vga-gold.png" },
      ] },
      { name: "DisplayPort", items: [
        { name: "DisplayPort 8K Copper Cables",         img: "/assets/generated/dp-8k-copper.png" },
        { name: "DisplayPort 8K AOC Cables",            img: "/assets/generated/dp-8k-aoc.png" },
        { name: "DP to HDMI Cables & Converters",       img: "/assets/generated/dp-to-hdmi-cable.png" },
        { name: "DP to VGA Cables & Converters",        img: "/assets/generated/dp-to-vga-cable.png" },
        { name: "DP to Mini DP Cables & Converters",    img: "/assets/generated/dp-to-minidp-cable.png" },
      ] },
      { name: "USB & Type-C", items: [
        { name: "USB Extension Cable 2.0, 3.0 & Active",      img: "/assets/generated/usb-extension.png" },
        { name: "USB Printer Cable 2.0, 3.0 & Active",        img: "/assets/generated/usb-printer.png" },
        { name: "USB to Type-C Cables",                       img: "/assets/generated/usb-to-typec.png" },
        { name: "USB Hubs 2.0 & 3.0",                         img: "/assets/generated/usb-hubs.png" },
        { name: "USB to Ethernet — Giga & Non-Giga",          img: "/assets/generated/usb-to-ethernet.png" },
        { name: "USB Peripherals",                            img: "/assets/generated/usb-peripherals.png" },
        { name: "Type-C Display Adapters",                    img: "/assets/generated/typec-display-adapter.png" },
        { name: "Type-C Docking Stations",                    img: "/assets/generated/typec-docking-station.png" },
        { name: "Type-C to Type-C Cable — 60W, 100W & 240W",  img: "/assets/generated/typec-to-typec.png" },
        { name: "Type-C Hubs 2.0 & 3.0",                      img: "/assets/generated/typec-hubs.png" },
        { name: "Type-C to Ethernet — Giga & Non-Giga",       img: "/assets/generated/typec-to-ethernet.png" },
        { name: "Type-C Peripherals",                         img: "/assets/generated/typec-peripherals.png" },
      ] },
      { name: "Power Cords", items: [
        { name: "Power Cord — Desktop",                  img: "/assets/generated/power-cord-desktop.png" },
        { name: "Power Cord — Laptop",                   img: "/assets/generated/power-cord-laptop.png" },
        { name: "Power Link Cords — C13, C14, C15",      img: "/assets/generated/power-link-cords.png" },
        { name: "Power Server Cord — C19, C20",          img: "/assets/generated/power-server-cord.png" },
        { name: "Power Cord — 16 Amps",                  img: "/assets/generated/power-cord-16amp.png" },
      ] },
    ]
  },
  {
    id: "peripherals", name: "Peripherals & Audio", Icon: Monitor, color: "#7C3AED", bg: "#F5F3FF",
    img: "https://images.unsplash.com/photo-1511467687858-23d96c32e4ae?w=900&q=80",
    desc: "Keyboards & mice, professional audio, monitors, mobility devices, gaming gear and conference-room camera systems",
    subcategories: [
      { name: "Keyboard & Mouse", items: [
        { name: "Wired Keyboard & Mouse Combo",     img: "/assets/generated/km-wired-combo.png" },
        { name: "Wireless Keyboard & Mouse Combo",  img: "/assets/generated/km-wireless-combo.png" },
        { name: "Mechanical Keyboard (Standalone)", img: "/assets/generated/km-mechanical.png" },
      ] },
      { name: "Speakers & Headphones", items: [
        { name: "Wired Headphones with Mic",           img: "/assets/generated/audio-wired-headphones.png" },
        { name: "Bluetooth Wireless Headphones",       img: "/assets/generated/audio-wireless-headphones.png" },
        { name: "Desktop Multimedia Stereo Speakers",  img: "/assets/generated/audio-speakers.png" },
      ] },
      { name: "Monitors", items: [
        { name: "22\" Full HD LED Business Monitor",   img: "/assets/generated/monitor-22-fhd.png" },
        { name: "27\" QHD IPS Monitor",                img: "/assets/generated/monitor-27-qhd.png" },
        { name: "32\" 4K UHD Curved Monitor",          img: "/assets/generated/monitor-32-4k.png" },
      ] },
      { name: "Mobility Products", items: [
        { name: "Business Laptop",              img: "/assets/generated/mobility-laptop.png" },
        { name: "2-in-1 Convertible Laptop",    img: "/assets/generated/mobility-2in1.png" },
        { name: "Enterprise Tablet",            img: "/assets/generated/mobility-tablet.png" },
      ] },
      { name: "Gaming Products", items: [
        { name: "Gaming Mechanical Keyboard (RGB)",  img: "/assets/generated/gaming-keyboard.png" },
        { name: "Gaming Mouse (High-DPI)",           img: "/assets/generated/gaming-mouse.png" },
        { name: "Gaming Headset with Mic",           img: "/assets/generated/gaming-headset.png" },
      ] },
      { name: "Conference Cameras", items: [
        { name: "1080P HD Conference Webcam",        img: "/assets/generated/conf-webcam.png" },
        { name: "4K PTZ Conference Camera",          img: "/assets/generated/conf-ptz.png" },
        { name: "Conference Video Bar (All-in-One)", img: "/assets/generated/conf-videobar.png" },
      ] },
    ]
  },
  {
    id: "networking", name: "Networking Infrastructure", Icon: Network, color: "#059669", bg: "#ECFDF5",
    img: "https://static.prod-images.emergentagent.com/jobs/807d24b9-8a0d-4ee2-b221-042517f188cc/images/a07deec238185167a9b66ddb86bad4ebd709e7b691f73a94c79759c68cd49cee.png",
    desc: "Structured cabling, Cat6 LAN cables, PoE switches, patch panels, PDUs, routers and rack enclosures for enterprise networks",
    subcategories: [
      { name: "LAN Cables", items: [
        { name: "Cat6 UTP LAN Cable (305m Roll)",                      img: "/assets/generated/lan-cable-cat6-utp.png" },
        { name: "Cat6A STP Shielded Cable (305m Roll)",                img: "/assets/generated/lan-cable-cat6a-stp.png" },
        { name: "Cat6 Patch Cord (1m / 2m / 3m / 5m up to 50m)",       img: "/assets/generated/lan-patch-cord-cat6.png" },
        { name: "Cat6 Outdoor LAN Cable (305m Roll)",                  img: "/assets/generated/lan-cable-cat6-outdoor.png" },
      ] },
      { name: "Patch Panel & Accessories", items: [
        { name: "24-Port Cat6 Patch Panel",       img: "/assets/generated/patch-panel-24port.png" },
        { name: "Cat6 Keystone Jacks (Pack)",     img: "/assets/generated/keystone-jack-cat6.png" },
        { name: "Crimping Tool",                  img: "/assets/generated/crimping-tool.png" },
        { name: "LAN Tester & Wire Tracker",      img: "/assets/generated/lan-tester.png" },
        { name: "1U Horizontal Cable Manager",    img: "/assets/generated/cable-manager-1u.png" },
        { name: "1U Horizontal Power Manager (PDU)", img: "/assets/generated/power-manager-1u.png" },
      ] },
      { name: "PoE Switches", items: [
        { name: "8-Port PoE Gigabit Switch",             img: "/assets/generated/switch-poe-8port.png" },
        { name: "16-Port PoE+ Gigabit Switch",           img: "/assets/generated/switch-poe-16port.png" },
        { name: "24-Port PoE+ Managed Gigabit Switch",   img: "/assets/generated/switch-poe-managed-24port.png" },
      ] },
      { name: "Managed Switches", items: [
        { name: "24-Port L2 Managed Gigabit Switch",   img: "/assets/generated/switch-managed-24port.png" },
        { name: "48-Port Managed Stackable Switch",    img: "/assets/generated/switch-managed-48port.png" },
        { name: "8-Port Industrial Managed Switch",    img: "/assets/generated/switch-industrial-managed.png" },
      ] },
      { name: "Routers & Wireless", items: [
        { name: "Wi-Fi 6 Dual-Band Router AX1800",  img: "/assets/generated/router-wifi6.png" },
        { name: "Ceiling Mount Access Point",       img: "/assets/generated/ap-ceiling-mount.png" },
        { name: "4G LTE Router with SIM Slot",      img: "/assets/generated/router-4g-lte.png" },
      ] },
      { name: "Racks & Enclosures", items: [
        { name: "Wall-Mount Rack (4U to 15U)",     img: "/assets/generated/rack-9u-wall.png" },
        { name: "Floor-Mount Server Rack (18U to 42U)", img: "/assets/generated/rack-42u-floor.png" },
      ] },
    ]
  },
  {
    id: "cctv", name: "CCTV & Surveillance", Icon: Camera, color: "#DC2626", bg: "#FEF2F2",
    img: "https://static.prod-images.emergentagent.com/jobs/807d24b9-8a0d-4ee2-b221-042517f188cc/images/dcf7426f583bb67af3f438e3f3c5c2a010b6740bd0a56d3fdd52ae5db48319f7.png",
    desc: "Hikvision IP cameras, DVR/NVR systems, SMPS power supplies, video baluns & server rack enclosures",
    subcategories: [
      { name: "Hikvision Cameras", items: [
        { name: "Hikvision 2MP Indoor Dome Camera",       img: "/assets/generated/cctv-dome-2mp.png" },
        { name: "Hikvision 4MP Outdoor Bullet Camera",    img: "/assets/generated/cctv-bullet-4mp.png" },
        { name: "Hikvision 8MP 4K IP Camera",             img: "/assets/generated/cctv-ip-4k.png" },
        { name: "Hikvision ColorVu Full-Color Camera",    img: "/assets/generated/cctv-colorvu.png" },
      ] },
      { name: "DVR / NVR Systems", items: [
        { name: "Hikvision 8-Channel DVR",       img: "/assets/generated/dvr-8ch.png" },
        { name: "Hikvision 16-Channel DVR",      img: "/assets/generated/dvr-16ch.png" },
        { name: "Hikvision 16-Channel 4K NVR",   img: "/assets/generated/nvr-16ch-4k.png" },
      ] },
      { name: "Power & Accessories", items: [
        { name: "12V SMPS Power Supply (5A / 10A)",         img: "/assets/generated/cctv-power-smps.png" },
        { name: "CCTV Power Distribution Box 8/16-Channel", img: "/assets/generated/cctv-power-box.png" },
        { name: "Video Balun Pair (BNC to UTP)",            img: "/assets/generated/cctv-video-balun.png" },
      ] },
      { name: "Racks & Enclosures", items: [
        { name: "PVC PoE Rack",           img: "/assets/generated/pvc-poe-rack.png" },
        { name: "Wall DVR Mount Rack",    img: "/assets/generated/wall-dvr-mount-rack.png" },
      ] },
    ]
  },
  {
    id: "kvm", name: "KVM & Enterprise Solutions", Icon: Server, color: "#D97706", bg: "#FFFBEB",
    img: "https://static.prod-images.emergentagent.com/jobs/807d24b9-8a0d-4ee2-b221-042517f188cc/images/eee18479d37825bb04e0bc64d54ef743fb69fe14276a8dbdcdaf7bebd45a72d0.png",
    desc: "ATEN KVM switches, thin clients, fiber media converters & enterprise-grade signal extension solutions",
    subcategories: [
      { name: "ATEN KVM Switches", items: [
        { name: "ATEN 2-Port USB HDMI KVM Switch",    img: "/assets/generated/kvm-2port-hdmi.png" },
        { name: "ATEN 8-Port USB VGA KVM Switch",     img: "/assets/generated/kvm-8port-vga.png" },
        { name: "ATEN 16-Port KVM over IP",           img: "/assets/generated/kvm-16port-ip.png" },
      ] },
      { name: "Thin Clients", items: [
        { name: "Enterprise Thin Client — Linux",   img: "/assets/generated/thin-client-linux.png" },
        { name: "Mini PC Thin Client — Windows",    img: "/assets/generated/thin-client-windows.png" },
      ] },
      { name: "Media Converters", items: [
        { name: "Single-Mode Fiber to Gigabit Ethernet Converter", img: "/assets/generated/media-converter-fiber.png" },
        { name: "16-Slot Fiber Media Converter Chassis",           img: "/assets/generated/media-converter-chassis.png" },
      ] },
      { name: "KVM Accessories & Extenders", items: [
        { name: "HDMI Extender over CAT6 (up to 60m)", img: "/assets/generated/hdmi-extender-cat6.png" },
        { name: "VGA Extender over UTP (up to 300m)",  img: "/assets/generated/vga-extender-utp.png" },
        { name: "Serial Console Server 8-Port",         img: "/assets/generated/serial-console-server.png" },
      ] },
    ]
  },
];

export default function Products() {
  const [searchParams] = useSearchParams();
  const catParam = searchParams.get("cat");
  const [activeId, setActiveId] = useState(catParam || "cables");
  const [activeSub, setActiveSub] = useState(0);

  useEffect(() => {
    if (catParam && CATEGORIES.find(c => c.id === catParam)) {
      setActiveId(catParam);
      setActiveSub(0);
    }
  }, [catParam]);

  const active = CATEGORIES.find(c => c.id === activeId) || CATEGORIES[0];

  return (
    <div>
      {/* Hero */}
      <section className="bg-[#2D3748] py-14 px-6" data-testid="products-hero">
        <div className="max-w-7xl mx-auto">
          <nav className="text-xs text-gray-500 mb-4">
            <Link to="/" className="hover:text-gray-300 transition-colors">Home</Link>
            <span className="mx-2">/</span>
            <span className="text-gray-300">Products</span>
          </nav>
          <h1 className="font-heading text-4xl lg:text-5xl font-black text-white mb-3">Product Catalogue</h1>
          <p className="text-gray-400 text-base max-w-xl">2000+ products across 5 verticals. Request a quote for bulk pricing.</p>
        </div>
      </section>

      <section className="py-10 bg-white" data-testid="products-main">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Sidebar tabs */}
            <aside className="lg:w-64 shrink-0">
              <h3 className="font-heading font-bold text-[#111827] text-xs uppercase tracking-widest mb-3">Categories</h3>
              <div className="space-y-1.5" data-testid="category-tabs">
                {CATEGORIES.map(({ id, name, Icon, color, bg }) => (
                  <button
                    key={id}
                    data-testid={`tab-${id}`}
                    onClick={() => { setActiveId(id); setActiveSub(0); }}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm font-medium transition-all duration-150 border ${
                      activeId === id
                        ? "bg-[#0F62FE] border-[#0F62FE] text-white shadow-md"
                        : "bg-[#F3F4F6] border-gray-200 text-[#374151] hover:border-[#0F62FE] hover:text-[#0F62FE]"
                    }`}
                  >
                    <Icon size={16} />
                    <span className="leading-snug">{name}</span>
                    {activeId === id && <ChevronRight size={14} className="ml-auto" />}
                  </button>
                ))}
              </div>

              {/* Quick CTA */}
              <div className="mt-6 p-4 bg-[#F3F4F6] border border-gray-200">
                <p className="text-xs font-bold text-[#111827] mb-2">Need bulk pricing?</p>
                <p className="text-xs text-[#4B5563] mb-3">Send us your requirement list for a custom quote.</p>
                <Link to="/contact" data-testid="products-quote-btn" className="block text-center bg-[#0F62FE] text-white text-xs font-semibold py-2.5 hover:bg-[#0052CC] transition-colors">
                  Request Quote
                </Link>
                <a
                  href={waLinkFor(active.name)}
                  target="_blank" rel="noreferrer"
                  data-testid="products-whatsapp-btn"
                  className="mt-2 flex items-center justify-center gap-1.5 bg-[#25D366] text-white text-xs font-semibold py-2.5 hover:bg-[#1fb655] transition-colors"
                >
                  <FaWhatsapp size={13} /> WhatsApp Us
                </a>
              </div>
            </aside>

            {/* Product panel */}
            <div className="flex-1">
              <div className="mb-6 pb-4 border-b border-gray-200">
                {/* Category banner image */}
                <div className="relative h-40 mb-4 overflow-hidden bg-gray-100">
                  <img src={active.img} alt={active.name} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-r from-[#2D3748]/70 to-transparent" />
                  <div className="absolute inset-0 flex items-center px-5">
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 bg-white/20 backdrop-blur-sm" style={{ borderLeft: `3px solid ${active.color}` }}>
                        <active.Icon size={22} className="text-white" />
                      </div>
                      <div>
                        <h2 className="font-heading text-xl font-black text-white">{active.name}</h2>
                        <p className="text-white/70 text-xs mt-0.5">
                          {active.subcategories.reduce((a, s) => a + s.items.length, 0)}+ products available
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <p className="text-[#4B5563] text-sm">{active.desc}</p>
              </div>

              {/* Sub-category tabs */}
              <div className="flex flex-wrap gap-2 mb-6" data-testid="subcategory-tabs">
                {active.subcategories.map((sub, idx) => (
                  <button
                    key={sub.name}
                    data-testid={`subtab-${idx}`}
                    onClick={() => setActiveSub(idx)}
                    className={`px-3 py-1.5 text-xs font-medium border transition-all duration-150 ${
                      activeSub === idx
                        ? "bg-[#2D3748] border-[#2D3748] text-white"
                        : "bg-white border-gray-200 text-[#374151] hover:border-[#2D3748]"
                    }`}
                  >
                    {sub.name}
                  </button>
                ))}
              </div>

              {/* Product list */}
              <motion.div
                key={`${activeId}-${activeSub}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                data-testid="product-list"
              >
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="font-heading font-bold text-[#111827] text-base">{active.subcategories[activeSub].name}</h3>
                  <span className="text-xs text-gray-400">{active.subcategories[activeSub].items.length} products</span>
                </div>
                <div className="grid sm:grid-cols-2 gap-2">
                  {active.subcategories[activeSub].items.map((raw, i) => {
                    const item = typeof raw === "string" ? { name: raw } : raw;
                    return (
                    <motion.div
                      key={item.name}
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.2, delay: i * 0.04 }}
                      className="flex items-center justify-between p-3 bg-[#F3F4F6] border border-gray-200 hover:border-[#0F62FE] hover:bg-white transition-all duration-150 group"
                      data-testid={`product-item-${i}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        {item.img ? (
                          <img
                            src={item.img}
                            alt={item.name}
                            loading="lazy"
                            className="w-14 h-14 shrink-0 object-cover bg-white border border-gray-200"
                          />
                        ) : (
                          <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: active.color }} />
                        )}
                        <span className="text-sm text-[#111827] font-medium leading-snug truncate">{item.name}</span>
                      </div>
                      <a
                        href={waLinkFor(item.name)}
                        target="_blank" rel="noreferrer"
                        className="shrink-0 ml-2 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Get Quote"
                        data-testid={`product-quote-${i}`}
                      >
                        <MessageCircle size={14} className="text-[#0F62FE]" />
                      </a>
                    </motion.div>
                    );
                  })}
                </div>
              </motion.div>

              {/* Bottom CTA */}
              <div className="mt-8 p-5 bg-[#EFF6FF] border border-blue-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <p className="font-bold text-[#111827] text-sm">Looking for a specific product or bulk quantity?</p>
                  <p className="text-xs text-[#4B5563] mt-0.5">Contact us for custom pricing and availability</p>
                </div>
                <div className="flex gap-3">
                  <Link to="/contact" data-testid="product-contact-btn" className="inline-flex items-center gap-1.5 bg-[#0F62FE] text-white text-xs font-semibold px-4 py-2.5 hover:bg-[#0052CC] transition-colors">
                    Contact Us <ArrowRight size={12} />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
